import * as React from 'react';

interface Props {
  src: string;
  title: string;
}

/**
 * Plays when mostly on screen; pauses when scrolled away.
 * Muted autoplay is required by browsers for autoplay without a user gesture.
 */
class StoryVideo extends React.Component<Props> {
  private videoRef: HTMLVideoElement | null = null;
  private observer: IntersectionObserver | null = null;

  private setVideoRef = (video: HTMLVideoElement | null) => {
    this.videoRef = video;
  }

  private playIfAllowed = () => {
    const video = this.videoRef;
    if (!video) {
      return;
    }
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Autoplay can still be blocked; leave controls available.
      });
    }
  }

  private pauseVideo = () => {
    const video = this.videoRef;
    if (!video) {
      return;
    }
    video.pause();
  }

  public componentDidMount() {
    const video = this.videoRef;
    if (!video || typeof IntersectionObserver === 'undefined') {
      this.playIfAllowed();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          this.playIfAllowed();
        } else {
          this.pauseVideo();
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    this.observer.observe(video);
  }

  public componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.pauseVideo();
  }

  public render() {
    const { src, title } = this.props;
    return (
      <video
        ref={this.setVideoRef}
        src={src}
        controls
        muted
        playsInline
        loop
        preload="metadata"
        title={title}
      />
    );
  }
}

export default StoryVideo;
